import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import AppShell from './AppShell';

const LobbyContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    padding-left: calc(16px + var(--safe-left));
    padding-right: calc(16px + var(--safe-right));
`;

const LobbyBox = styled.div`
    background: var(--tg-surface);
    padding: 16px;
    border-radius: var(--tg-radius);
    border: 1px solid var(--tg-border);
    box-shadow: var(--tg-shadow);
    width: 100%;
`;

const LobbyInput = styled.input`
    padding: 12px;
    font-size: 16px;
    border-radius: var(--tg-radius-sm);
    border: 1px solid var(--tg-border);
    background: var(--tg-surface-2);
    color: var(--tg-text);
    width: 100%;
`;

const JoinButton = styled.button`
    padding: 12px 18px;
    font-size: 16px;
    border-radius: var(--tg-radius-sm);
    border: none;
    background: var(--tg-accent);
    color: var(--tg-text);
    cursor: pointer;
`;

const Row = styled.div`
    display: flex;
    gap: 12px;
    width: 100%;
`;

const Subtle = styled.div`
    color: var(--tg-muted);
    font-size: 12px;
    text-align: left;
    margin-bottom: 10px;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Item = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 12px;
    border-radius: var(--tg-radius);
    border: 1px solid var(--tg-border);
    background: rgba(255,255,255,0.04);
    color: var(--tg-text);
    cursor: pointer;
    text-align: left;

    &:hover {
        filter: brightness(1.05);
    }
`;

const Left = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const RoomTitle = styled.div`
    font-weight: 700;
    font-size: 14px;
`;

const RoomMeta = styled.div`
    font-size: 12px;
    color: var(--tg-muted);
`;

const Action = styled.button`
    border: 1px solid var(--tg-border);
    background: rgba(255,255,255,0.06);
    color: var(--tg-text);
    border-radius: var(--tg-radius-sm);
    padding: 8px 10px;
    cursor: pointer;
`;

const Lobby = () => {
    const [roomId, setRoomId] = useState('');
    const [recentRooms, setRecentRooms] = useState([]);
    const navigate = useNavigate();

    const origin = useMemo(() => {
        try {
            return window.location.origin;
        } catch {
            return '';
        }
    }, []);

    useEffect(() => {
        try {
            const raw = localStorage.getItem('recentRooms');
            const parsed = raw ? JSON.parse(raw) : [];
            if (Array.isArray(parsed)) setRecentRooms(parsed);
        } catch {
            setRecentRooms([]);
        }
    }, []);

    const persistRecent = (rooms) => {
        setRecentRooms(rooms);
        try {
            localStorage.setItem('recentRooms', JSON.stringify(rooms));
        } catch {
            // ignore
        }
    };

    const addRecent = (id) => {
        const clean = id.trim();
        if (!clean) return;
        const next = [clean, ...recentRooms.filter((r) => r !== clean)].slice(0, 12);
        persistRecent(next);
    };

    const joinRoom = () => {
        if (roomId.trim() !== '') {
            const clean = roomId.trim();
            addRecent(clean);
            navigate(`/room/${clean}`);
        }
    };

    const openRoom = (id) => {
        addRecent(id);
        navigate(`/room/${id}`);
    };

    const copyLink = async (id) => {
        const link = `${origin}/room/${id}`;
        try {
            await navigator.clipboard.writeText(link);
        } catch {
            // ignore
        }
    };

    return (
        <AppShell title="Connect">
            <LobbyContainer>
                <LobbyBox>
                    <Subtle>Create / Join a room</Subtle>
                    <Row>
                        <LobbyInput 
                            type="text" 
                            placeholder="Room ID"
                            value={roomId}
                            onChange={(e) => setRoomId(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && joinRoom()}
                        />
                        <JoinButton onClick={joinRoom}>Join</JoinButton>
                    </Row>
                </LobbyBox>

                <LobbyBox>
                    <Subtle>Recent rooms</Subtle>
                    <List>
                        {recentRooms.length === 0 ? (
                            <RoomMeta>No rooms yet. Join one to see it here.</RoomMeta>
                        ) : (
                            recentRooms.map((id) => (
                                <Item key={id} onClick={() => openRoom(id)}>
                                    <Left>
                                        <RoomTitle>Room {id}</RoomTitle>
                                        <RoomMeta>Tap to join</RoomMeta>
                                    </Left>
                                    <Action
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            copyLink(id);
                                        }}
                                    >
                                        Copy link
                                    </Action>
                                </Item>
                            ))
                        )}
                    </List>
                </LobbyBox>
            </LobbyContainer>
        </AppShell>
    );
};

export default Lobby;
